import { scry } from "@/lib/scryClient";
// CommunityFeed.tsx (rebased from CommunityFeedPublic + Cards "marketplace" view)
// (Normalized to pure v9 Firestore modular usage with aliased orderBy)
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { auth, db } from "@/lib/firebase";
import {
  getFirestore,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy as fbOrderBy,   // aliased to ensure Firestore orderBy is used
  limit,
  startAfter,
  doc,
  getDoc,
  type QueryDocumentSnapshot,
  type DocumentData
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import NewMiniProfileCard from "@/components/community/NewMiniProfileCard";
import { Button } from "@/components/ui/button";
import ListingProfileCard from "@/components/community/ListingProfileCard";
import EventsPage from "@/pages/Events"; // already added

// Works whether `db` is compat or modular. Falls back to default app if needed.
const db9 = ((db as any)?._delegate ?? db) || getFirestore();

/* ------------------------------- Constants ------------------------------- */
const MTG_FORMATS = [
  "Commander","cEDH","Canadian Highlander","Modern","Standard","Pioneer","Legacy","Vintage",
  "Pauper","Draft","Sealed","MTG Arena","Explorer","Historic","Alchemy","D&D (non-MTG)"
];

// map friendly format names -> scryfall/legalities keys (best-effort)
const FORMAT_KEY_MAP: Record<string, string> = {
  Commander: "commander",
  cEDH: "commander",
  Modern: "modern",
  Standard: "standard",
  Pioneer: "pioneer",
  Legacy: "legacy",
  Vintage: "vintage",
  Pauper: "pauper",
  "MTG Arena": "arena",
  Explorer: "explorer",
  Historic: "historic",
  Alchemy: "alchemy",
  // fallback will use a simplified normalization
};

const HIDDEN_USERNAMES = new Set(["scoreboard-bot"]);
const HIDDEN_OWNER_UIDS = new Set(["scoreboard-bot"]); // assume UID matches username

type Profile = {
  uid: string;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  rank_title?: string | null;
  preferences?: { available_for_trading?: boolean; available_for_play?: boolean; };
  location?: { lat?: number | null; lng?: number | null };
  play_formats?: string[];
};

type ViewMode = "feed" | "grid" | "cards" | "events"; // ADDED
const PAGE = 48;
// We fetch up to ~60 cards per load (3 lanes × 20 each) but show 24 at a time in the UI.
const CARDS_PAGE_SIZE = 20;

function useDebounced<T>(v: T, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

/* --------------------------- Cards view types ---------------------------- */
type GlobalListing = {
  id: string;
  ownerUid: string;
  name: string;
  name_lower: string;
  image: string | null;
  set: string | null;
  number: string | number | null;
  foil: boolean;
  status: "sale" | "trade";
  price: number | null;
  scryfall_id: string | null;
  oracle_id: string | null;
  createdAt?: any;
};
type CardsListingExt = GlobalListing & {
  createdAtMs: number;
  updatedAtMs: number;
  lastActivityMs: number;
  distance?: number;
  legalities?: Record<string, string>;
  types?: string[];
};

/* ------------------------------ Component -------------------------------- */
export default function CommunityFeed(): JSX.Element {
  /* ------------------------------- View mode ------------------------------ */
  const [view, setView] = useState<ViewMode>("feed");
  const [showCalendar, setShowCalendar] = useState(false); // collapsible calendar (default collapsed)
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const calendarToggleRef = useRef<HTMLButtonElement | null>(null); // NEW

  // Collapsible preferences toolbar (collapsed by default)
  const [toolbarCollapsed, setToolbarCollapsed] = useState<boolean>(true);

  // Click‑away + Esc to close calendar
  useEffect(() => {
    if (!showCalendar) return;
    function onPointer(e: MouseEvent | TouchEvent) {
      const tgt = e.target as Node;
      if (calendarRef.current && calendarRef.current.contains(tgt)) return;
      if (calendarToggleRef.current && calendarToggleRef.current.contains(tgt)) return;
      setShowCalendar(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setShowCalendar(false);
    }
    document.addEventListener("mousedown", onPointer, true);
    document.addEventListener("touchstart", onPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer, true);
      document.removeEventListener("touchstart", onPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [showCalendar]);

  /* ------------------------------- Filters -------------------------------- */
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 250);
  const [eventDate, setEventDate] = useState<Date | null>(null);

  /* ------------------------------- Global Radius -------------------------- */
  const [radiusMi, setRadiusMi] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem("cf_radiusMi");
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? (n >= 200 ? null : n) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (radiusMi == null) localStorage.removeItem("cf_radiusMi");
      else localStorage.setItem("cf_radiusMi", String(radiusMi));
    } catch {}
  }, [radiusMi]);

  // ADD (fix ReferenceError: selectedProfileUid / profileSelectOpen / visibilityFilter)
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "public" | "private">("all");
  const [selectedProfileUid, setSelectedProfileUid] = useState<string | null>(null);
  const [profileSelectOpen, setProfileSelectOpen] = useState(false);

  // ADD: state for expanded profile (fixes ReferenceError)
  const [expandedProfileUid, setExpandedProfileUid] = useState<string | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setExpandedProfileUid(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Lock body scroll while overlay open
  useEffect(() => {
    if (!expandedProfileUid) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [expandedProfileUid]);

  // NEW: listen for QuickSearchOverlay community filter events (immediate update)
  useEffect(() => {
    function onCommunityFilter(e: any) {
      const term = (e?.detail?.term ?? "").toString();
      setSearch(term); // immediate so Cards view reflects instantly
    }
    window.addEventListener("app:communityFilter", onCommunityFilter as any);
    return () => window.removeEventListener("app:communityFilter", onCommunityFilter as any);
  }, []);

  const [formats, setFormats] = useState<string[]>([]);
  const toggleFormat = useCallback((f: string) => {
    setFormats(prev => prev.includes(f) ? prev.filter(x => x !== f) : prev.concat(f));
  }, []);
  const clearFilters = () => { setFormats([]); setSearch(""); };

  // NEW: card type selector state (only used by Cards view) — placed alongside formats so effects can reference it safely
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const toggleCardType = useCallback((t: string) => {
    setCardTypes(prev => (prev.includes(t) ? prev.filter(x => x !== t) : prev.concat(t)));
  }, []);

  /* ---- Exclude my profile/cards toggle (persisted across sessions) ------- */
  const [excludeSelf, setExcludeSelf] = useState<boolean>(() => {
    // FIX: default to false (was true) so initial cards load is visible when user only has their own cards
    try {
      const raw = localStorage.getItem("cf_excludeSelf");
      if (raw === null) return false;        // <-- changed (previously defaulted to true)
      return raw !== "0";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem("cf_excludeSelf", excludeSelf ? "1" : "0"); } catch {}
  }, [excludeSelf]);

  /* ------------------------------- LGS Filter ------------------------------ */
  const [lgsFilter, setLgsFilter] = useState<"all" | "only" | "hide">("all");
  const cycleLgs = () =>
    setLgsFilter(p => {
      setProfileSelectOpen(false); // closes profile dropdown when cycling
      return p === "all" ? "only" : p === "only" ? "hide" : "all";
    });

  /* ------------------------------ Auth user ------------------------------- */
  const [meUid, setMeUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  useEffect(() => onAuthStateChanged(auth, u => setMeUid(u?.uid ?? null)), []);

  /* ------------------------------ Location -------------------------------- */
  const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (meUid) {
          const snap = await getDoc(doc(db9, "users", meUid)); // switched to db9
          const v: any = snap.data();
          const lat = typeof v?.location?.lat === "number" ? v.location.lat : null;
          const lng = typeof v?.location?.lng === "number" ? v.location.lng : null;
          if (!cancelled && lat != null && lng != null) { setMyLoc({ lat, lng }); return; }
        }
      } catch {}
      if (!cancelled && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => !cancelled && setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => {}
        );
      }
    })();
    return () => { cancelled = true; };
  }, [meUid]);

  /* ------------------------------ Profiles data --------------------------- */
  const [rows, setRows] = useState<Profile[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const cursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const loadingRef = useRef(false);

  const loadProfiles = useCallback(async (initial = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      if (initial) cursorRef.current = null;
      const base = query(                                    // use db9 + fbOrderBy
        collection(db9, "users"),
        fbOrderBy("__name__"),
        limit(PAGE)
      );
      const qy = cursorRef.current ? query(base, startAfter(cursorRef.current)) : base;
      const snap = await getDocs(qy);
      if (snap.docs.length) cursorRef.current = snap.docs.at(-1)!;
      const page: Profile[] = snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) }));
      const pageFiltered = page.filter(p => {
        const uname = (p.username || p.display_name || "").toLowerCase();
        return !HIDDEN_USERNAMES.has(uname);
      });
      setRows(prev => {
        const map = new Map<string, Profile>();
        (initial ? [] : prev).forEach(p => map.set(p.uid, p));
        pageFiltered.forEach(p => map.set(p.uid, p));
        return Array.from(map.values());
      });
      setHasMore(snap.docs.length === PAGE);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    setRows([]); setHasMore(false); cursorRef.current = null;
    void loadProfiles(true);
  }, [loadProfiles]);

  /* ------------------------------ Distance util (now module scope) --------------------------- */
  const R_EARTH_MI = 3958.7613;
  const toRad = (d: number) => (d * Math.PI) / 180;
  function distMiles(aLat: number, aLng: number, bLat: number, bLng: number) {
    const dLat = toRad(bLat - aLat), dLon = toRad(bLng - aLng);
    const s = Math.sin, c = Math.cos;
    const h = s(dLat/2)**2 + c(toRad(aLat))*c(toRad(bLat))*s(dLon/2)**2;
    return 2 * R_EARTH_MI * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  // --- START PATCH: SCRYFALL BACKEND HELPERS ---
/** Defensive JSON unwrap */
async function toJsonOrNull(res: Response | null | undefined) {
  try {
    if (!res || !("ok" in res) || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Prefer normal/large; supports multi-face; skip when image_status === "missing" */
function pickScryImage(sf: any): string | null {
  if (!sf || sf.image_status === "missing") return null;
  const direct = sf?.image_uris?.normal ?? sf?.image_uris?.large ?? null;
  if (direct) return direct;
  if (Array.isArray(sf?.card_faces)) {
    for (const face of sf.card_faces) {
      const img = face?.image_uris?.normal ?? face?.image_uris?.large ?? null;
      if (img) return img;
    }
  }
  return null;
}

/** Backend-first fetch by oracle_id, fallback to public Scryfall */
async function fetchByOracleBackendFirst(oracleId: string): Promise<any | null> {
  // Try backend client – feature detect likely methods
  try {
    if (typeof (scry as any)?.getByOracleId === "function") {
      const res = await (scry as any).getByOracleId(oracleId);
      const json = await toJsonOrNull(res);
      if (json?.id) return json;
    }
    if (typeof (scry as any)?.getCard === "function") {
      const res = await (scry as any).getCard({ oracle_id: oracleId });
      const json = await toJsonOrNull(res);
      if (json?.id) return json;
    }
  } catch {
    // ignore & fallback
  }

  // Public fallback
  try {
    const r = await fetch(`https://api.scryfall.com/cards/oracle/${encodeURIComponent(oracleId)}`);
    return await toJsonOrNull(r);
  } catch {
    return null;
  }
}
// --- END PATCH: SCRYFALL BACKEND HELPERS ---


  /* ------------------------------- Cards view ----------------------------- */
  const [cardsListings, setCardsListings] = useState<CardsListingExt[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const cardsLoadingRef = useRef(false);
  const [cardsHasMore, setCardsHasMore] = useState(false);
  const [cardsError, setCardsError] = useState<string | null>(null);

  // Cursor refs for the 3 marketplace lanes (status / for_sale / for_trade)
  const cardsCursorsRef = useRef<{
    status: QueryDocumentSnapshot<DocumentData> | null;
    flagSale: QueryDocumentSnapshot<DocumentData> | null;
    flagTrade: QueryDocumentSnapshot<DocumentData> | null;
  }>({ status: null, flagSale: null, flagTrade: null });

  // Reset helper used before initial / re-loads
  function resetCardsCursorsAll() {
    cardsCursorsRef.current = { status: null, flagSale: null, flagTrade: null };
  }


  // NEW: distance owner location caches (fix ownerLocs ReferenceError)
  const [ownerLocs, setOwnerLocs] = useState<Record<string, { lat: number; lng: number } | null>>({});
  const ownerLocsRef = useRef(ownerLocs);
  useEffect(() => { ownerLocsRef.current = ownerLocs; }, [ownerLocs]);
  const myLocRef = useRef(myLoc);
  useEffect(() => { myLocRef.current = myLoc; }, [myLoc]);

  // NEW: per‑owner filter + visible count (24 at a time)
  const [cardOwnerFilter, setCardOwnerFilter] = useState<string | null>(null);
  const [showAllOwners, setShowAllOwners] = useState(true); // NEW toggle
  const [cardsVisibleCount, setCardsVisibleCount] = useState(24);

  // Reset visible slice when core filters change (keep smooth UX)
  useEffect(() => {
    if (view === "cards") setCardsVisibleCount(24);
  }, [view, search, debounced, excludeSelf, cardOwnerFilter, showAllOwners, formats, cardTypes]);

  // Helper to resolve owner display name from already-loaded profiles
  const ownerName = useCallback(
    (uid: string) => {
      const p = rows.find(r => r.uid === uid);
      return p?.display_name || p?.username || uid.slice(0, 6);
    },
    [rows]
  );

  // (Owner avatar optional; not required but can be uncommented if needed)
  // const ownerAvatar = useCallback(
  //   (uid: string) => rows.find(r => r.uid === uid)?.avatar_url || null,
  //   [rows]
  // );

  // NEW: listen for QuickSearchOverlay community filter events (immediate update)
  useEffect(() => {
    function onCommunityFilter(e: any) {
      const term = (e?.detail?.term ?? "").toString();
      setSearch(term); // immediate so Cards view reflects instantly
    }
    window.addEventListener("app:communityFilter", onCommunityFilter as any);
    return () => window.removeEventListener("app:communityFilter", onCommunityFilter as any);
  }, []);

  // Marketplace triple-lane loader
  const loadCards = useCallback(async (initial: boolean) => {
    if (cardsLoadingRef.current) return;
    cardsLoadingRef.current = true;
    setCardsLoading(true);

    if (initial) {
      resetCardsCursorsAll();
      setCardsListings([]);
      setCardsError(null);
    }

    try {
      // Use a larger per-lane page for the initial load (like Marketplace)
      const LANE_PAGE = initial ? PAGE : CARDS_PAGE_SIZE;

      // We'll iterate fetches on initial load until we collect a useful number
      const ownerFromPath = (path: string) => {
        const segs = path.split("/");
        const uIdx = segs.indexOf("users");
        if (uIdx >= 0 && segs[uIdx + 1]) return segs[uIdx + 1];
        const cIdx = segs.lastIndexOf("cards");
        if (cIdx > 0 && segs[cIdx - 1]) return segs[cIdx - 1];
        return null;
      };

      const KNOWN_TYPES = ["creature","instant","sorcery","enchantment","artifact","planeswalker","battle","land"];
      const assimilateDoc = (d: QueryDocumentSnapshot<DocumentData>, out: Map<string, CardsListingExt>) => {
        const v: any = d.data();
        const ownerUid = ownerFromPath(d.ref.path) || v.ownerUid || "unknown";
        if (HIDDEN_OWNER_UIDS.has(ownerUid)) return;
        if (excludeSelf && meUid && ownerUid === meUid) return;

        const name = v.name || v.card_name || "Card";
        const legalities: Record<string, string> =
          v.scryfall_full?.legalities || v.legalities || {};
        const typeLine: string = v.type_line || v.scryfall_full?.type_line || (v.card_type || "") || "";
        const typeParts = typeLine.toLowerCase().split(/[^a-z]+/).filter(Boolean);
        const types = typeParts.filter(p => KNOWN_TYPES.includes(p));
        const createdAtMs =
          v.createdAt?.toMillis?.() ?? (typeof v.createdAt === "number" ? v.createdAt : 0);
        const updatedAtMs =
          v.updatedAt?.toMillis?.() ?? (typeof v.updatedAt === "number" ? v.updatedAt : createdAtMs);
        const status: "sale" | "trade" =
          v.status === "sale" || v.status === "trade" ? v.status : v.for_sale === true ? "sale" : "trade";

        const entry: CardsListingExt = {
          id: d.id,
          ownerUid,
          name,
          name_lower: (v.name_lower || name).toLowerCase(),
          image:
            v.custom_image_url ||
            v.image ||
            v.card_image ||
            v.scryfall_full?.image_uris?.normal ||
            v.scryfall_full?.image_uris?.small ||
            null,
          set: v.set ?? v.set_code ?? null,
          number: v.number ?? v.collector_number ?? null,
          foil: !!(v.foil || v.isFoil),
          status,
          price: typeof v.price === "number" ? v.price : null,
          scryfall_id: v.scryfall_id || null,
          oracle_id: v.oracle_id || null,
          createdAt: v.createdAt || null,
          createdAtMs,
          updatedAtMs,
          lastActivityMs: createdAtMs,
          legalities,
          types,
          distance: undefined,
        };
        out.set(`${entry.ownerUid}:${entry.id}`, entry);
      };

      // Prepare bases once (limit will be applied per-iteration via LANE_PAGE)
      const statusBase = query(
        collectionGroup(db9, "cards"),
        where("status", "in", ["sale", "trade"]),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );
      const saleBase = query(
        collectionGroup(db9, "cards"),
        where("for_sale", "==", true),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );
      const tradeBase = query(
        collectionGroup(db9, "cards"),
        where("for_trade", "==", true),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );

      const cumulative = new Map<string, CardsListingExt>();
      let iterations = 0;
      const TARGET = 24;
      let anyLaneFilledLast = false;
      do {
        const qStatus = cardsCursorsRef.current.status
          ? query(statusBase, startAfter(cardsCursorsRef.current.status))
          : statusBase;
        const qSale = cardsCursorsRef.current.flagSale
          ? query(saleBase, startAfter(cardsCursorsRef.current.flagSale))
          : saleBase;
        const qTrade = cardsCursorsRef.current.flagTrade
          ? query(tradeBase, startAfter(cardsCursorsRef.current.flagTrade))
          : tradeBase;

        const [snapStatus, snapSale, snapTrade] = await Promise.all([
          getDocs(qStatus),
          getDocs(qSale),
          getDocs(qTrade),
        ]);

        anyLaneFilledLast =
          snapStatus.docs.length === LANE_PAGE ||
          snapSale.docs.length === LANE_PAGE ||
          snapTrade.docs.length === LANE_PAGE;

        if (snapStatus.docs.length) cardsCursorsRef.current.status = snapStatus.docs.at(-1)!;
        if (snapSale.docs.length) cardsCursorsRef.current.flagSale = snapSale.docs.at(-1)!;
        if (snapTrade.docs.length) cardsCursorsRef.current.flagTrade = snapTrade.docs.at(-1)!;

        for (const d of snapStatus.docs) assimilateDoc(d, cumulative);
        for (const d of snapSale.docs) assimilateDoc(d, cumulative);
        for (const d of snapTrade.docs) assimilateDoc(d, cumulative);

        iterations++;
      } while (initial && iterations < 4 && cumulative.size < TARGET && anyLaneFilledLast);

      const merged = Array.from(cumulative.values());
      setCardsHasMore(anyLaneFilledLast);

      // --- START PATCH: SCRYFALL BACKEND ENRICH ---
      // Enrich via backend-first Scryfall (legalities/types/image), fallback to public
      try {
        const need = new Map<string, string>(); // oracle_id -> key(owner:id)
        for (const c of merged) {
          const lacksLegal = !c.legalities || Object.keys(c.legalities).length === 0;
          const lacksTypes = !c.types || c.types.length === 0;
          const lacksImage = !c.image;
          if ((lacksLegal || lacksTypes || lacksImage) && c.oracle_id) {
            need.set(c.oracle_id, `${c.ownerUid}:${c.id}`);
          }
        }

        if (need.size) {
          const fetches = Array.from(need.keys()).map(async (oid) => {
            const data = await fetchByOracleBackendFirst(oid);
            return data ? { oid, data } : null;
          });

          const results = await Promise.all(fetches);
          const byOid = new Map<string, any>();
          for (const r of results) if (r && r.oid && r.data) byOid.set(r.oid, r.data);

          if (byOid.size) {
            for (let i = 0; i < merged.length; i++) {
              const c = merged[i];
              if (!c.oracle_id) continue;
              const s = byOid.get(c.oracle_id);
              if (!s) continue;

              // legalities
              if (!c.legalities || Object.keys(c.legalities).length === 0) {
                if (s.legalities && typeof s.legalities === "object") {
                  c.legalities = { ...s.legalities };
                }
              }

              // types (from type_line)
              if (!c.types || c.types.length === 0) {
                const typeLine = (s.type_line || "").toString().toLowerCase();
                const parts = typeLine.split(/[^a-z]+/).filter(Boolean);
                const KNOWN_TYPES = ["creature","instant","sorcery","enchantment","artifact","planeswalker","battle","land"];
                const types = parts.filter(p => KNOWN_TYPES.includes(p));
                if (types.length) c.types = types;
              }

              // image (only if missing)
              if (!c.image) {
                const img = pickScryImage(s);
                if (img) c.image = img;
              }
            }
          }
        }
      } catch (e) {
        console.debug("[CommunityFeed] scryfall enrich (backend-first) failed", e);
      }
// --- END PATCH: SCRYFALL BACKEND ENRICH ---


      // Merge into state (initial replaces, subsequent appends/merge)
      setCardsListings(prev => {
        const map = new Map<string, CardsListingExt>();
        (initial ? [] : prev).forEach(c => map.set(`${c.ownerUid}:${c.id}`, c));
        merged.forEach(c => map.set(`${c.ownerUid}:${c.id}`, c));
        let arr = Array.from(map.values());
        if (myLocRef.current) {
          arr = arr.map(c => {
            const loc = ownerLocsRef.current[c.ownerUid];
            if (!loc) return c;
            const dmi = distMiles(myLocRef.current.lat, myLocRef.current.lng, loc.lat, loc.lng);
            return { ...c, distance: dmi };
          });
        }
        arr.sort((a, b) =>
          b.createdAtMs - a.createdAtMs ||
          (a.distance ?? Infinity) - (b.distance ?? Infinity) ||
          a.name_lower.localeCompare(b.name_lower)
        );
        return arr;
      });
      if (initial) setCardsVisibleCount(Math.min(24, Math.max(merged.length, cumulative.size)));

    } catch (e: any) {
      console.error("[CommunityFeed] loadCards failed:", e);
      const code = String(e?.code || "").toLowerCase();
      if (code.includes("permission")) setCardsError("Permission denied (rules).");
      else if (code.includes("failed-precondition")) setCardsError("Missing index for marketplace queries.");
      else setCardsError("Failed to load cards.");
    } finally {
      setCardsLoading(false);
      cardsLoadingRef.current = false;
    }
  }, [excludeSelf, meUid]);

  // Initial load
  useEffect(() => {
  if (view === "cards" && cardsListings.length === 0 && !cardsLoading) {
    void loadCards(true);
  }
}, [view, cardsListings.length, cardsLoading, loadCards]);

  // Updated excludeSelf effect to use new reset
  useEffect(() => {
    if (view !== "cards") return;
    resetCardsCursorsAll();
    setCardsListings([]);
    void loadCards(true);
  }, [excludeSelf, view, loadCards]);

  // Resort distances when location/owner locations change
  useEffect(() => {
    if (!cardsListings.length) return;
    setCardsListings(prev => {
      const arr = prev.map(c => {
                   if (!myLocRef.current) return c;
          const owners = ownerLocsRef.current;
          if (!owners) return c;
          const loc = owners[c.ownerUid];
          if (!loc) return c;
        const dmi = distMiles(
          myLocRef.current.lat,
          myLocRef.current.lng,
          loc.lat,
          loc.lng
        );
        return { ...c, distance: dmi };
      }).sort((a, b) =>
        b.createdAtMs - a.createdAtMs ||
        (a.distance ?? Infinity) - (b.distance ?? Infinity) ||
        a.name_lower.localeCompare(b.name_lower)
      );
      return arr;
    });
  }, [ownerLocs, myLoc, cardsListings.length]);

  /* ------------------------------ Profiles filtering ---------------------- */
  const filteredProfiles = useMemo(() => {
    let list = rows;

    if (formats.length) {
      const pick = new Set(formats.map(s => s.toLowerCase().trim()));
      list = list.filter(p => {
        const they = (p.play_formats || []).map(s => (s || "").toLowerCase().trim());
        for (const f of pick) if (they.includes(f)) return true;
        return false;
      });
    }

    if (debounced.trim()) {
      const q = debounced.trim().toLowerCase();
      list = list.filter(p =>
        (p.username || "").toLowerCase().includes(q) ||
        (p.display_name || "").toLowerCase().includes(q)
      );
    }

    if (excludeSelf && meUid) {
      list = list.filter(p => p.uid !== meUid);
    }

    if (lgsFilter !== "all") {
      list = list.filter(p =>
        lgsFilter === "only" ? isLgsProfile(p) : !isLgsProfile(p)
      );
    }

    if (radiusMi != null && myLoc) {
      list = list.filter(p => {
        const plat = typeof p.location?.lat === "number" ? p.location.lat : null;
        const plng = typeof p.location?.lng === "number" ? p.location.lng : null;
        if (plat == null || plng == null) return false;
        return distMiles(myLoc.lat, myLoc.lng, plat, plng) <= radiusMi;
      });
    }

    if (myLoc) {
      list = [...list].sort((a, b) => {
        const aLat = typeof a.location?.lat === "number" ? a.location.lat : null;
        const aLng = typeof a.location?.lng === "number" ? a.location.lng : null;
        const bLat = typeof b.location?.lat === "number" ? b.location.lat : null;
        const bLng = typeof b.location?.lng === "number" ? b.location.lng : null;
        const ad = aLat != null && aLng != null ? distMiles(myLoc.lat, myLoc.lng, aLat, aLng) : Infinity;
        const bd = bLat != null && bLng != null ? distMiles(myLoc.lat, myLoc.lng, bLat, bLng) : Infinity;
        return ad - bd;
      });
    }

    if (selectedProfileUid) {
      list = list.filter(p => p.uid === selectedProfileUid);
    }

    list = list.filter(p => {
      const uname = (p.username || p.display_name || "").toLowerCase();
      return !HIDDEN_USERNAMES.has(uname);
    });

    return list;
  }, [
    rows,
    formats,
    debounced,
    excludeSelf,
    meUid,
    lgsFilter,
    radiusMi,
    myLoc,
    selectedProfileUid
  ]);

  // Derive unique owner list for owner chips (exclude duplicates)
  const cardOwnerChips = useMemo(() => {
    const set = new Set<string>();
    for (const c of cardsListings) {
      if (HIDDEN_OWNER_UIDS.has(c.ownerUid)) continue;
      if (excludeSelf && meUid && c.ownerUid === meUid) continue;
      set.add(c.ownerUid);
      if (set.size >= 200) break; // cap
    }
    return Array.from(set);
  }, [cardsListings, excludeSelf, meUid]);

  // Derive profiles to show in the top profile selector, scoped to current view
  const profilesForSelector = useMemo(() => {
    // Helper to resolve uid -> Profile (skip hidden / missing)
    const resolve = (uid: string | null) => (uid ? rows.find(r => r.uid === uid) || null : null);

    if (view === "cards") {
      // show only owners who currently have visible card listings
      const uids = cardOwnerChips; // already excludes self/bots and caps at 200
      const out: Profile[] = [];
      const seen = new Set<string>();
      for (const uid of uids) {
        if (seen.has(uid)) continue;
        const p = resolve(uid);
        if (p) {
          out.push(p);
          seen.add(uid);
        }
      }
      // Ensure selected profile (if any) is included
      if (selectedProfileUid && !seen.has(selectedProfileUid)) {
        const p = resolve(selectedProfileUid);
        if (p) out.unshift(p);
      }
      return out;
    }

    // For feed / grid / events use the filtered profile list (already applies formats, radius, search, LGS, etc.)
    const base = filteredProfiles || [];
    const map = new Map<string, Profile>();
    for (const p of base) {
      if (!map.has(p.uid)) map.set(p.uid, p);
      if (map.size >= 200) break;
    }
    // Ensure selected profile is present
    if (selectedProfileUid && !map.has(selectedProfileUid)) {
      const sel = resolve(selectedProfileUid);
      if (sel) map.set(sel.uid, sel);
    }
    return Array.from(map.values()).slice(0, 200);
  }, [view, rows, filteredProfiles, cardOwnerChips, selectedProfileUid]);

  // Base filtered cards (search + radius + selectedProfileUid)
  const cardsFilteredBase = useMemo(() => {
    let list = cardsListings.filter(c => !HIDDEN_OWNER_UIDS.has(c.ownerUid));

    // Global profile filter
    if (selectedProfileUid) {
      list = list.filter(c => c.ownerUid === selectedProfileUid);
    }

    // Radius filter
    if (radiusMi != null && myLoc) {
      list = list.filter(c => {
        if (typeof c.distance === "number") return c.distance <= radiusMi;
        const loc = ownerLocs[c.ownerUid];
        if (!loc) return true; // keep until distance known
        const dmi = distMiles(myLoc.lat, myLoc.lng, loc.lat, loc.lng);
        return dmi <= radiusMi;
      });
    }

    // Text search (debounced)
    if (debounced.trim()) {
      const q = debounced.toLowerCase();
      list = list.filter(c =>
        c.name_lower.includes(q) ||
        (c.set || "").toLowerCase().includes(q) ||
        (c.status || "").toLowerCase().includes(q)
      );
    }

    // Format legality filter (use formats selector to filter cards by scryfall legalities)
    if (formats.length) {
      list = list.filter(c => {
        if (!c.legalities) return false;
        for (const f of formats) {
          const key = FORMAT_KEY_MAP[f] || f.toLowerCase().replace(/[^a-z]/g, "");
          if (c.legalities[key] === "legal") return true;
        }
        return false;
      });
    }

    // Card type filter (only show when specific types selected)
    if (cardTypes.length) {
      list = list.filter(c => {
        if (!c.types || !c.types.length) return false;
        for (const t of cardTypes) {
          if (c.types.includes(t)) return true;
        }
        return false;
      });
    }

    return list;
  }, [cardsListings, selectedProfileUid, radiusMi, myLoc, ownerLocs, debounced, formats, cardTypes]);

  // Owner filter layer
  const cardsAfterOwner = useMemo(() => {
    if (showAllOwners || !cardOwnerFilter) return cardsFilteredBase;
    return cardsFilteredBase.filter(c => c.ownerUid === cardOwnerFilter);
  }, [cardsFilteredBase, showAllOwners, cardOwnerFilter]);

  // Page slice
  const pagedCards = useMemo(
    () => cardsAfterOwner.slice(0, cardsVisibleCount),
    [cardsAfterOwner, cardsVisibleCount]
  );

  // Simple preview image map for grid cards (fallback first card image per owner)
  const cardPreviewImages = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const c of cardsListings) {
      if (!(c.ownerUid in map)) {
        map[c.ownerUid] = c.image || null;
      }
    }
    return map;
  }, [cardsListings]);

  /* -------------------------------- Render -------------------------------- */
  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-20 border-b border-white/10 bg-black/70 backdrop-blur-md">
        <div className="mx-auto w-full max-w-5xl px-3 py-3 relative">
          <div className="flex flex-col gap-3">

            {/* Row 1: View selector + Toolbar toggle (titles moved below) */}
            <div className="flex flex-wrap items-center gap-2">
              {(["feed","cards","grid","events"] as ViewMode[]).map(m => {
                const active = view === m;
                return (
                  <button
                    key={m}
                    onClick={() => setView(m)}
                    className={
                      "relative group select-none font-semibold tracking-wide transition-all duration-300 " +
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:ring-white/50 " +
                      "rounded-xl border text-sm px-5 py-1 flex items-center justify-center shadow-md " +
                      getViewButtonClasses(m, active)
                    }
                    aria-pressed={active}
                  >
                    <span className="drop-shadow-sm">
                      {m === "feed"  && "Feed"}
                      {m === "cards" && "Cards"}
                      {m === "grid"  && "Grid"}
                      {m === "events"&& "Events"}
                    </span>
                  </button>
                );
              })}

              {/* Toolbar toggle now inline (moved from left margin) */}
              <button
                type="button"
                onClick={() => setToolbarCollapsed(c => !c)}
                aria-expanded={!toolbarCollapsed}
                aria-controls="community-toolbar"
                title={toolbarCollapsed ? "Show preferences" : "Hide preferences"}
                className={
                  "rounded-lg border border-white/20 px-3 py-1 text-sm font-semibold " +
                  "bg-white/10 hover:bg-white/15 text-white transition"
                }
              >
                {toolbarCollapsed ? "Preferences +" : "Close –"}
              </button>
            </div>

            {/* Row 2: Collapsible preferences toolbar (now also contains Profile selector) */}
            <div
              id="community-toolbar"
              className={
                toolbarCollapsed
                  ? "hidden"
                  : "flex flex-col gap-4 w-full"
              }
            >
              {/* Top controls row: visibility + reset */}
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-[10px] uppercase tracking-wide text-white/50">
                  Visibility
                </label>
                <select
                  value={visibilityFilter}
                  onChange={e => setVisibilityFilter(e.target.value as any)}
                  className="bg-black/40 text-xs rounded border border-white/15 px-2 py-1 text-white/80 focus:outline-none focus:ring-1 focus:ring-white/30"
                  title="Filter events by visibility"
                >
                  <option value="all">All</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormats([]);
                      setSearch("");
                      setRadiusMi(null);
                      setVisibilityFilter("all");
                      setLgsFilter("all");
                      setSelectedProfileUid(null);
                      setEventDate(null);
                      setExcludeSelf(false);
                      setCardTypes([]);
                    }}
                  >
                    Reset
                  </Button>
              </div>

              {/* Radius + toggles */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                  <label className="text-[10px] uppercase tracking-wide text-white/50">Radius</label>
                  <input
                    type="range"
                    min={5}
                    max={200}
                    step={5}
                    value={radiusMi ?? 200}
                    onChange={e => {
                      const v = Number(e.target.value);
                      setRadiusMi(v >= 200 ? null : v);
                    }}
                    className="h-4"
                    title="Filter by distance"
                  />
                  <div className="text-[11px] w-14 text-center text-white/60">
                    {radiusMi == null ? "Any" : `${radiusMi}mi`}
                  </div>
                  {radiusMi != null && (
                    <button
                      type="button"
                      className="text-[10px] px-1 py-0.5 border border-white/10 rounded hover:bg-white/10 text-white/60"
                      onClick={() => setRadiusMi(null)}
                      title="Clear radius"
                    >
                      ×
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setExcludeSelf(v => !v)}
                  aria-pressed={excludeSelf}
                  className={
                    "rounded px-2 py-1 text-xs border " +
                    (excludeSelf
                      ? "border-amber-400/50 bg-amber-500/20 text-amber-200"
                      : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                  }
                  title="Show / hide my own listings"
                >
                  My Cards: {excludeSelf ? "Hidden" : "Shown"}
                </button>

                <button
                  type="button"
                  onClick={cycleLgs}
                  className={
                    "rounded px-2 py-1 text-xs border " +
                    (lgsFilter === "only"
                      ? "border-emerald-400/50 bg-emerald-500/20 text-emerald-200"
                      : lgsFilter === "hide"
                      ? "border-red-400/50 bg-red-500/20 text-red-200"
                      : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                  }
                  title="Cycle LGS profiles filter"
                >
                  LGS: {lgsFilter === "all" ? "All" : lgsFilter === "only" ? "Only" : "Hide"}
                </button>
              </div>

              {/* Formats */}
              <div className="flex flex-wrap gap-2">
                {MTG_FORMATS.map(f => {
                  const active = formats.includes(f);
                  return (
                    <button
                      key={f}
                      onClick={() => toggleFormat(f)}
                      className={
                        "rounded-full border px-3 py-1 text-xs transition " +
                        (active
                          ? "border-white/40 bg-white/15 text-white"
                          : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                      }
                    >
                      {f}
                    </button>
                  );
                })}
              </div>

              {/* Card types (cards view only) */}
              {view === "cards" && (
                <div className="flex flex-wrap gap-2">
                  {["Creature","Instant","Sorcery","Enchantment","Artifact","Planeswalker","Battle","Land"].map(t => {
                    const key = t.toLowerCase();
                    const active = cardTypes.includes(key);
                    return (
                      <button
                        key={t}
                        onClick={() => toggleCardType(key)}
                        className={
                          "rounded-full border px-3 py-1 text-xs transition " +
                          (active
                            ? "border-white/40 bg-white/15 text-white"
                            : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                        }
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Profile selector (moved INSIDE collapsible toolbar) */}
              <div className="no-scrollbar flex w-full gap-2 overflow-x-auto pb-1 items-center">
                <button
                  type="button"
                  onClick={() => setSelectedProfileUid(null)}
                  className={
                    "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition " +
                    (!selectedProfileUid
                      ? "border-indigo-400/50 bg-indigo-500/25 text-indigo-100"
                      : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                  }
                  title="Show listings from any profile"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                    Any
                  </span>
                  <span className="truncate">Any</span>
                </button>

                {profilesForSelector.map(p => {
                  const name = p.display_name || p.username || p.uid.slice(0,6);
                  const avatar = p.avatar_url || null;
                  const active = selectedProfileUid === p.uid;
                  return (
                    <button
                      key={p.uid}
                      type="button"
                      onClick={() => setSelectedProfileUid(active ? null : p.uid)}
                      className={
                        "group flex items-center gap-2 rounded-full border pr-3 pl-1 py-1 text-xs transition max-w-[200px] " +
                        (active
                          ? "border-indigo-400/50 bg-indigo-500/25 text-indigo-100"
                          : "border-white/15 bg-black/40 text-white/70 hover:bg-white/10")
                      }
                      title={name}
                    >
                      <span className="relative inline-flex h-7 w-7 overflow-hidden rounded-full border border-white/10 bg-white/5 shrink-0">
                        {avatar ? (
                          <img
                            src={avatar}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                            draggable={false}
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[10px] text-white/50 font-semibold">
                            {name.slice(0,2).toUpperCase()}
                          </span>
                        )}
                      </span>
                      <span className="truncate">{name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 3: View Title (moved OUTSIDE toolbar & always visible) */}
            <div className="flex flex-wrap items-end gap-3 pt-1">
              {view === "feed" && (
                <>
                  <div className="text-lg font-extrabold text-white">Feed</div>
                  <div className="text-sm text-white/60">Nearby Users</div>
                </>
              )}
              {view === "cards" && (
                <>
                  <div className="text-lg font-extrabold text-white">Cards</div>
                  <div className="text-sm text-white/60">Nearby cards available</div>
                </>
              )}
              {view === "grid" && (
                <>
                  <div className="text-lg font-extrabold text-white">Grid</div>
                  <div className="text-sm text-white/60">Full list of Nearby Users</div>
                </>
              )}
              {view === "events" && (
                <>
                  <div className="text-lg font-extrabold text-white">Events</div>
                  <div className="text-sm text-white/60">Nearby Events</div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl flex-1 overflow-y-auto px-2 py-3">
        {view === "feed" && (
          <div className="flex flex-col gap-4">
            {filteredProfiles.map(p => (
              <NewMiniProfileCard key={p.uid} profile={p} />
            ))}
            {hasMore && (
              <div className="flex items-center justify-center py-6">
                <Button variant="outline" onClick={() => loadProfiles(false)} disabled={loading}>
                  {loading ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </div>
        )}

        {view === "grid" && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filteredProfiles.map(p => (
              <div
                key={p.uid}
                className="col-span-1 cursor-pointer transition-transform hover:scale-[1.02]"
                onClick={() => setExpandedProfileUid(p.uid)}
              >
                <NewMiniProfileCard
                  profile={p}
                  gridMode
                  compactGrid
                  cardPreviewUrl={cardPreviewImages[p.uid]}
                />
              </div>
            ))}
            {hasMore && (
              <div className="col-span-full flex items-center justify-center py-6">
                <Button variant="outline" onClick={() => loadProfiles(false)} disabled={loading}>
                  {loading ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </div>
        )}

        {view === "cards" && (
          <div className="flex flex-col gap-4">
            

            <div className="text-xs text-white/50">
              Cards: {cardsAfterOwner.length}
              {debounced && cardsAfterOwner.length !== cardsListings.length && " (filtered)"}
              {cardsLoading && " (loading...)"}
              {!showAllOwners && cardOwnerFilter && " (owner filtered)"}
            </div>

            {cardsError && (
              <div className="text-red-400 text-sm border border-red-500/40 rounded p-2">
                {cardsError}
              </div>
            )}

            {cardsAfterOwner.length === 0 && !cardsLoading && !cardsError && (
              <div className="text-center text-sm text-white/60 py-6">
                {debounced
                  ? "No cards match your search."
                  : cardOwnerFilter
                  ? "This user has no visible cards."
                  : "No card listings to display."}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pagedCards.map(card => {
                const listing = {
                  id: card.id,
                  name: card.name,
                  image: card.image,
                  status: card.status,
                  price: card.price,
                  foil: card.foil,
                  set: card.set,
                  number: card.number,
                  scryfall_id: card.scryfall_id || undefined,
                  oracle_id: card.oracle_id || undefined,
                };
                 // Provide a richer profile stub so ListingProfileCard can link by username
                const owner = rows.find(r => r.uid === card.ownerUid);
                const profileStub = {
                  uid: card.ownerUid,
                  username: owner?.username ?? undefined,
                  display_name: owner?.display_name ?? undefined,
                  avatar_url: owner?.avatar_url ?? undefined,
                } as any;
                return (
                  <ListingProfileCard
                    key={card.ownerUid + ":" + card.id}
                    profile={profileStub}
                    listing={listing as any}
                    distanceMiles={card.distance ?? null}
                  />
                );
              })}
            </div>

            {/* Client-side pagination (24 at a time) */}
            {cardsVisibleCount < cardsAfterOwner.length && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cardsLoading}
                  onClick={() =>
                    setCardsVisibleCount(v => Math.min(v + 24, cardsAfterOwner.length))
                  }
                >
                  Show More
                </Button>
              </div>
            )}

            {/* Server load more (only when we've shown all current + more exist server-side) */}
            {cardsVisibleCount >= cardsAfterOwner.length &&
              cardsHasMore &&
              !cardsError && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cardsLoading}
                    onClick={() => loadCards(false)}
                  >
                    {cardsLoading ? "Loading…" : "Load more"}
                  </Button>
                </div>
              )}

            {cardsError && (
              <div className="flex flex-col items-center gap-2">
                <div className="text-xs text-red-300">{cardsError}</div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cardsLoading}
                  onClick={() => {
                    resetCardsCursorsAll();
                    void loadCards(true);
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {view === "events" && (
          <div className="w-full relative">
            <div className="flex justify-end mb-2">
              <button
                ref={calendarToggleRef} // ADDED
                type="button"
                onClick={() => setShowCalendar(c => !c)}
                className={
                  "rounded px-3 py-1.5 text-xs font-medium border transition " +
                  (showCalendar
                    ? "border-amber-400/60 bg-amber-500/30 text-amber-100"
                    : "border-white/15 bg-black/40 text-white/70 hover:bg-white/15")
                }
                title="Toggle calendar"
              >
                {showCalendar ? "Hide Calendar ▲" : "Show Calendar ▼"}
              </button>
            </div>
            {showCalendar && (
              <div
                ref={calendarRef}
                className="mb-3 md:absolute md:top-0 md:right-0 md:z-10 md:mb-0"
              >
                <InlineMonthCalendar
                  selected={eventDate}
                  onSelect={(d) =>
                    setEventDate(prev =>
                      prev && d && sameDay(prev, d) ? null : d
                    )
                  }
                />
              </div>
            )}
            <EventsPage
              selectedDate={eventDate}
              searchTerm={search}
              radiusMi={radiusMi}
              selectedFormats={formats}
              visibilityFilter={visibilityFilter}
              lgsFilter={lgsFilter}
              selectedProfileUid={selectedProfileUid}
            />
          </div>
        )}
      </div>

      {/* Expanded profile overlay (grid zoom) */}
      {expandedProfileUid && (
        <div
          className="fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
          onClick={() => setExpandedProfileUid(null)}
        >
          <div
            className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-black/80 shadow-2xl backdrop-blur-md scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedProfileUid(null)}
              className="absolute top-2 right-2 z-10 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-xs text-white border border-white/20 shadow"
            >
              Close
            </button>
            <div className="p-4 sm:p-5">
              {(() => {
                const p =
                  filteredProfiles.find(pr => pr.uid === expandedProfileUid) ||
                  rows.find(pr => pr.uid === expandedProfileUid);
                return p ? (
                  <NewMiniProfileCard profile={p} />
                ) : (
                  <div className="rounded border border-white/20 bg-black/60 p-4 text-sm text-white/60">
                    Profile not found.
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Gradient button styles */
        .view-btn-base{color:#fff;}
        /* Feed: Majestic purple gradient (example deep → vibrant) */
        .view-feed{
          background:linear-gradient(135deg,#4c1d95 0%,#6d28d9 40%,#7e22ce 70%,#a855f7 100%);
          border-color:#a855f7cc;
        }
        .view-feed.inactive{
          background:linear-gradient(135deg,#2d165a 0%,#3b2175 60%,#4c277d 100%);
          opacity:.65;
          border-color:#4c1d9580;
        }
        /* Cards: black → charcoal gradient */
        .view-cards{
          background:linear-gradient(140deg,#111827 0%,#1f2937 55%,#374151 100%);
          border-color:#6b7280aa;
        }
        .view-cards.inactive{
          background:linear-gradient(140deg,#0b0f17 0%,#141b22 60%,#222b33 100%);
          opacity:.65;
          border-color:#37415166;
        }
        /* Grid: electric blue gradient */
        .view-grid{
          background:linear-gradient(135deg,#0ea5e9 0%,#0284c7 45%,#0369a1 85%);
          border-color:#38bdf8cc;
        }
        .view-grid.inactive{
          background:linear-gradient(135deg,#073753 0%,#0a4d73 70%,#0e5d88 100%);
          opacity:.65;
          border-color:#0ea5e980;
        }
        /* Events: “Event Sunrise” Light/Dark fusion gradient */
        .view-events{
          background:linear-gradient(120deg,#f59e0b 0%,#ea580c 38%,#f97316 60%,#0ea5e9 100%);
          border-color:#f59e0bcc;
        }
        .view-events.inactive{
          background:linear-gradient(120deg,#7a4304 0%,#86330a 40%,#a84610 70%,#0d4f68 100%);
          opacity:.65;
          border-color:#ea580c80;
        }
        .view-btn-base.inactive:hover{
          opacity:.9;
          filter:brightness(1.15);
        }
        .view-btn-base:not(.inactive){box-shadow:0 0 0 1px #ffffff22,0 4px 14px -4px #000,0 0 24px -6px rgba(255,255,255,.25);}
        .view-btn-base:active{transform:translateY(1px);}
      `}</style>
    </div>
  );
}

/* ------------------------------ Helpers ---------------------------------- */
function isLgsProfile(p: Profile) {
  return (p as any).is_lgs ||
    (p as any).account_type === "lgs" ||
    (p.rank_title || "").toLowerCase().includes("lgs");
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

type InlineMonthCalendarProps = {
  selected: Date | null;
  onSelect: (d: Date) => void;
};

function InlineMonthCalendar({ selected, onSelect }: InlineMonthCalendarProps) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // current month only (per spec)
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0 Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className="m-3 rounded-lg border border-white/10 bg-black/70 backdrop-blur-md p-3 shadow-lg w-[238px]">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-white/80">
          {today.toLocaleString(undefined, {
            month: "long",
            year: "numeric",
          })}
        </div>
        {selected && (
          <button
            onClick={() => onSelect(selected)}
            className="text-[10px] text-amber-300 hover:text-amber-200"
            title="Click selected date in grid to clear"
          >
            Selected
          </button>
        )}
      </div>
      <div className="grid grid-cols-7 gap-[2px] text-[10px] text-white/40 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`wd-${i}`} className="text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-[4px] text-xs">
        {cells.map((d, idx) => {
          if (!d)
            return <div key={idx} className="h-7 text-center text-white/15" />;
          const isToday = sameDay(d, today);
            const isSel = selected && sameDay(d, selected);
          return (
            <button
              key={idx}
              onClick={() => onSelect(d)}
              className={
                "h-7 rounded-md flex items-center justify-center transition border text-[11px] " +
                (isSel
                  ? "border-amber-400/60 bg-amber-500/30 text-amber-100"
                  : isToday
                  ? "border-white/30 bg-white/10 text-white"
                  : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10")
              }
              title={
                isSel
                  ? "Click to clear date filter"
                  : "Filter events by this date"
              }
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-white/30 border border-white/30" />
          <span className="text-[10px] text-white/50">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-amber-500/40 border border-amber-400/60" />
          <span className="text-[10px] text-white/50">Selected</span>
        </div>
      </div>
    </div>
  );
}

// Add this helper inside the file (outside component or above return)
function getViewButtonClasses(m: ViewMode, active: boolean) {
  const base = "view-btn-base ";
  switch (m) {
    case "feed":
      return base + (active ? "view-feed" : "view-feed inactive");
    case "cards":
      return base + (active ? "view-cards" : "view-cards inactive");
    case "grid":
      return base + (active ? "view-grid" : "view-grid inactive");
    case "events":
      return base + (active ? "view-events" : "view-events inactive");
    default:
      return base;
  }
}


